/**
 * DepartmentSelect — выпадающий список подразделений структуры компании с созданием нового
 * подразделения ПРЯМО В СПИСКЕ (centrika-docs!26, `component.create-profrisk-order.positions`,
 * разделы «Выбор подразделения» и «Добавление нового подразделения»).
 *
 * Состав раскрытого списка: первой строкой акцентная «+ Добавить новое подразделение», за ней
 * разделительная черта, ниже — существующие подразделения. Нажатие акцентной строки ЗАМЕНЯЕТ её
 * компактным блоком создания (поле «Наименование подразделения» + «Добавить»/«Отмена») — отдельное
 * модальное окно не используется. Фокус уходит в поле, `Enter` создаёт, `Escape` отменяет создание
 * и возвращает обычный список. Ошибка создания (в т.ч. дубль) оставляет список и блок открытыми,
 * введённое наименование сохраняется.
 *
 * Реализация — WAI-ARIA combobox по образцу `EmployeeSelect`/`PositionSelect` (там же обоснование
 * портала и `pointer-events` внутри Radix Dialog). Значение поля — ТЕКСТ наименования
 * подразделения (в должности профрисков хранится именно он), поэтому значение вне списка
 * (свободный текст старого черновика) показывается как есть.
 *
 * Escape: перехват через `useComboboxEscape` — иначе Radix Dialog, внутри которого поле может
 * быть смонтировано, закрылся бы раньше (грабли centrika-docs!22). Хук вызывается ТРИЖДЫ: закрыть
 * список (обычное состояние) и отменить создание — отдельно для портала со списком и отдельно для
 * самого поля. Оба контейнера нужны, потому что блок создания живёт в портале (в `anchorRef` не
 * входит), но фокус при этом может стоять и на поле: focus-trap модального диалога возвращает
 * фокус в своё поддерево, а портал списка в него не входит. Отмена создания возвращает фокус на
 * поле — иначе он улетает на `body` и следующий Escape перехватить уже некому.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Department } from '@/lib/api'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'
import { RequiredMark } from '@/design-system/components/primitives/field'
import { useComboboxEscape } from './use-combobox-escape'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { cn } from '@/lib/utils'

/** Подпись акцентной опции создания подразделения (дословно). */
export const DEPARTMENT_SELECT_ADD = '+ Добавить новое подразделение'
/** Подпись поля блока создания и его placeholder (дословно). */
export const DEPARTMENT_SELECT_NAME_LABEL = 'Наименование подразделения'
export const DEPARTMENT_SELECT_NAME_PLACEHOLDER = 'Например, Участок №2'
/** Кнопки блока создания (дословно). */
export const DEPARTMENT_SELECT_CREATE = 'Добавить'
export const DEPARTMENT_SELECT_CANCEL = 'Отмена'
/** Возврат к списку в режиме отложенного создания (centrika-docs!61, дословно). */
export const DEPARTMENT_SELECT_BACK_TO_LIST = 'Добавить из списка'
/**
 * Форма «Добавить нового сотрудника» (centrika-docs!70) требует иных ДОСЛОВНЫХ подписей отложенного
 * режима, чем форма рабочего места СОУТ (!61): placeholder «Например, Цех №1» и ссылку «Выбрать из
 * списка». Расхождение — конфликт частных требований двух форм; общий компонент остаётся один, а
 * подписи прокидываются через `entryPlaceholder`/`backToListLabel` (по умолчанию — строки !61, чтобы
 * форма СОУТ не менялась).
 */
export const DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER = 'Например, Цех №1'
export const DEPARTMENT_SELECT_CHOOSE_FROM_LIST = 'Выбрать из списка'
/** Ошибка дубля — текст ИНТЕРФЕЙСА (сервер отдаёт общий 409, см. structure-manager.tsx). */
export const DEPARTMENT_SELECT_DUPLICATE = 'Такое подразделение уже есть'
/** Пустой результат поиска по списку подразделений. */
export const DEPARTMENT_SELECT_EMPTY = 'Подразделения не найдены'

export interface DepartmentSelectProps {
  /** Текущее значение — наименование подразделения (пусто, если не выбрано). */
  value: string
  /** Актуальный список подразделений структуры (общий для всех полей экрана). */
  departments: Department[]
  /**
   * Выбор/ввод подразделения. `name` — наименование; `departmentId` задан ТОЛЬКО когда выбрано
   * существующее подразделение из списка (centrika-docs!61) — при свободном вводе нового имени
   * (deferCreate) он undefined, что позволяет вызывающему отличить выбор существующего от
   * ввода нового (и заблокировать дубль).
   */
  onSelect: (name: string, departmentId?: string) => void
  /**
   * Создание подразделения. Резолв — созданное подразделение (вызывающий добавляет его в общий
   * список), reject — ошибка показывается внутри блока создания. Не задан — акцентной опции нет.
   */
  onCreate?: (name: string) => Promise<Department>
  /** Текст ошибки создания по коду ответа (дубль → «Такое подразделение уже есть»). */
  createErrorText?: (err: unknown) => string
  /**
   * Режим отложенного создания (centrika-docs!61): «+ Добавить новое подразделение» открывает
   * поле ввода наименования (значение уходит через `onSelect` как свободный текст) и кнопку
   * «Добавить из списка» для возврата к выбору; само подразделение создаётся ПОЗЖЕ вместе с
   * рабочим местом. `onCreate` в этом режиме не используется; дубль показывается сразу.
   */
  deferCreate?: boolean
  /**
   * Placeholder поля ввода наименования в отложенном режиме (`deferCreate`). По умолчанию строка
   * !61 «Например, Участок №2»; форма добавления сотрудника (!70) передаёт
   * `DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER` — «Например, Цех №1».
   */
  entryPlaceholder?: string
  /**
   * Подпись ссылки возврата к списку в отложенном режиме. По умолчанию строка !61 «Добавить из
   * списка»; форма добавления сотрудника (!70) передаёт `DEPARTMENT_SELECT_CHOOSE_FROM_LIST` —
   * «Выбрать из списка».
   */
  backToListLabel?: string
  placeholder?: string
  id?: string
  'aria-label'?: string
  'aria-invalid'?: boolean | undefined
  'aria-describedby'?: string | undefined
  /** Обязательность для вспомогательных технологий: маркер «*» у подписи от них скрыт. */
  'aria-required'?: true | undefined
  invalid?: boolean
  disabled?: boolean
}

export function DepartmentSelect({
  value,
  departments,
  onSelect,
  onCreate,
  createErrorText,
  deferCreate,
  entryPlaceholder = DEPARTMENT_SELECT_NAME_PLACEHOLDER,
  backToListLabel = DEPARTMENT_SELECT_BACK_TO_LIST,
  placeholder = 'Выберите подразделение',
  id,
  invalid,
  disabled,
  ...aria
}: DepartmentSelectProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  // Активная строка для клавиатурной навигации (-1 — ничего не подсвечено).
  const [active, setActive] = useState(-1)
  const [creating, setCreating] = useState(false)
  // Отложенный ввод нового подразделения (centrika-docs!61): основное поле превращается в
  // свободный ввод (не портал — фокус не теряется внутри модального диалога).
  const [deferredEntry, setDeferredEntry] = useState(false)
  const [newName, setNewName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const newNameRef = useRef<HTMLInputElement>(null)
  // Блок создания живёт в портале: blur самого поля при уходе фокуса туда НЕ должен закрывать
  // список (иначе форма создания исчезала бы в момент фокуса).
  const creatingRef = useRef(false)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const reactId = useId()
  const listboxId = `${reactId}-listbox`
  const rowId = (i: number) => `${reactId}-opt-${i}`

  useEffect(() => () => clearTimeout(blurTimer.current ?? undefined), [])

  useEffect(() => {
    creatingRef.current = creating
  }, [creating])

  const options = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? departments.filter((d) => d.name.toLowerCase().includes(q)) : departments
  }, [departments, query])

  // Сброс подсветки при смене запроса/открытия — активная строка не «залипает» вне диапазона.
  useEffect(() => {
    setActive(-1)
  }, [query, open])

  const visible = open && !disabled
  // В открытом состоянии инпут показывает вводимый фильтр, в закрытом — выбранное значение.
  const inputValue = open ? query : value

  useLayoutEffect(() => {
    if (!visible) return
    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setPos({ top: r.bottom + 4, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible])

  // Прокрутка активной строки в видимую область при навигации с клавиатуры.
  useEffect(() => {
    if (!visible || active < 0) return
    const el = document.getElementById(rowId(active))
    // scrollIntoView отсутствует в jsdom — вызываем опционально.
    el?.scrollIntoView?.({ block: 'nearest' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, visible])

  // «После открытия блока фокус устанавливается в поле «Наименование подразделения»».
  useEffect(() => {
    if (creating) newNameRef.current?.focus()
  }, [creating])

  function cancelCreate() {
    setCreating(false)
    creatingRef.current = false
    setNewName('')
    setCreateError(null)
  }

  /**
   * Отмена создания по действию пользователя («Отмена», Escape): блок исчезает вместе с полем, в
   * котором стоял фокус, поэтому возвращаем фокус в само поле — иначе фокус улетает на `body`,
   * и следующий Escape (закрыть список) уже некому перехватить, а клавиатурный пользователь
   * теряет позицию. При закрытии всего списка (`close`) возвращать фокус не нужно.
   */
  function cancelCreateAndFocus() {
    cancelCreate()
    inputRef.current?.focus()
  }

  function close() {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    setOpen(false)
    setQuery('')
    setActive(-1)
    cancelCreate()
  }

  // Escape закрывает только список, а не модальный диалог вокруг поля — см. докстринг хука.
  // Шкала шрифтов портального списка (centrika-docs!31): класс берётся из контекста, а не
  // ставится безусловно — портал живёт в `document.body`, вне DOM-поддерева рабочей области.
  const workScale = useWorkScaleClass()
  useComboboxEscape(visible && !creating, anchorRef, close)
  // «Клавиша Escape отменяет создание и возвращает обычный список подразделений». Перехват нужен
  // ОБОИМ контейнерам: блок создания отрисован в портале (проверка по элементу списка), но фокус
  // может остаться и на самом поле — например, внутри модального диалога, чей focus-trap
  // возвращает фокус в своё поддерево, а портал списка в него не входит.
  useComboboxEscape(visible && creating, listRef, cancelCreateAndFocus)
  useComboboxEscape(visible && creating, anchorRef, cancelCreateAndFocus)

  // Пока открыт блок создания, blur поля список не закрывает — закрытие по клику мимо
  // (иначе форма создания «залипала» бы до выбора подразделения).
  useEffect(() => {
    if (!visible || !creating) return
    const onPointerDown = (e: Event) => {
      const target = e.target as Node | null
      if (!target) return
      if (anchorRef.current?.contains(target) || listRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, creating])

  function choose(dept: Department) {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    // Выбор существующего из списка — отдаём и id, чтобы вызывающий отличил его от ввода нового.
    onSelect(dept.name, dept.id)
    close()
  }

  const canCreate = newName.trim().length > 0 && !submitting
  // Есть ли служебная опция создания (немедленное onCreate или отложенное deferCreate).
  const hasCreate = Boolean(onCreate) || Boolean(deferCreate)
  // Отложенный ввод: введённое наименование дублирует существующее (без регистра/пробелов) —
  // показываем ошибку (значение всё равно резолвится вызывающим в id существующего).
  const deferredDupEntry =
    deferredEntry &&
    value.trim() !== '' &&
    departments.some((d) => d.name.trim().toLowerCase() === value.trim().toLowerCase())

  async function create() {
    if (!onCreate || !canCreate) return
    setCreateError(null)
    setSubmitting(true)
    try {
      const created = await onCreate(newName.trim())
      // Успех: подразделение выбирается в текущей форме, список закрывается (обновление
      // списка во всех формах шага — забота вызывающего, он владеет `departments`).
      onSelect(created.name)
      close()
    } catch (err) {
      // «Выпадающий список и блок создания остаются открытыми, введённое наименование
      // сохраняется и отображается сообщение об ошибке».
      setCreateError(createErrorText?.(err) ?? DEPARTMENT_SELECT_DUPLICATE)
    } finally {
      setSubmitting(false)
    }
  }

  function openCreate() {
    setCreateError(null)
    if (deferCreate) {
      // Отложенный режим: закрываем список, включаем свободный ввод в ОСНОВНОМ поле (в дереве
      // диалога — фокус-трап не отбирает фокус, в отличие от портального блока). Значение уже
      // выбранного существующего подразделения сбрасываем — пользователь вводит новое.
      setOpen(false)
      const isExisting = departments.some((d) => d.name === value)
      if (isExisting) onSelect('')
      setDeferredEntry(true)
      setTimeout(() => inputRef.current?.focus(), 0)
      return
    }
    setCreating(true)
    creatingRef.current = true
  }

  /** Возврат к списку из отложенного ввода: сбрасывает введённое наименование и открывает список. */
  function backToList() {
    onSelect('')
    setDeferredEntry(false)
    setOpen(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (!open) {
          setOpen(true)
          return
        }
        if (options.length > 0) setActive((a) => (a < 0 ? 0 : Math.min(a + 1, options.length - 1)))
        break
      case 'ArrowUp':
        e.preventDefault()
        if (open && options.length > 0) setActive((a) => (a <= 0 ? 0 : a - 1))
        break
      case 'Enter':
        if (open && active >= 0 && active < options.length) {
          e.preventDefault()
          choose(options[active])
        }
        break
      // Escape обрабатывается в фазе захвата на window (`useComboboxEscape`).
      default:
        break
    }
  }

  const activeDescendant = visible && active >= 0 ? rowId(active) : undefined

  // Отложенный ввод нового подразделения: основное поле — свободный текст (в дереве диалога).
  if (deferCreate && deferredEntry) {
    return (
      <div ref={anchorRef} className="flex flex-col gap-1.5">
        <Input
          ref={inputRef}
          id={id}
          value={value}
          placeholder={entryPlaceholder}
          invalid={invalid || deferredDupEntry}
          disabled={disabled}
          aria-label={aria['aria-label']}
          aria-invalid={aria['aria-invalid']}
          aria-describedby={aria['aria-describedby']}
          aria-required={aria['aria-required']}
          autoComplete="off"
          onChange={(e) => onSelect(e.target.value)}
        />
        {deferredDupEntry ? (
          <p role="alert" className="text-12 text-red">
            {DEPARTMENT_SELECT_DUPLICATE}
          </p>
        ) : null}
        {/* Возврат к выбору существующего подразделения. */}
        <button
          type="button"
          className="self-start text-12 text-accent hover:underline disabled:opacity-50"
          disabled={disabled}
          onClick={backToList}
        >
          {backToListLabel}
        </button>
      </div>
    )
  }

  return (
    <div ref={anchorRef}>
      <Input
        ref={inputRef}
        id={id}
        value={inputValue}
        // Открытое поле держит выбранное значение подсказкой: запрос очищен, а видно, что выбрано.
        placeholder={open && value ? value : placeholder}
        invalid={invalid}
        disabled={disabled}
        aria-label={aria['aria-label']}
        aria-invalid={aria['aria-invalid']}
        aria-describedby={aria['aria-describedby']}
        aria-required={aria['aria-required']}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={visible}
        aria-controls={visible ? listboxId : undefined}
        aria-activedescendant={activeDescendant}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        // Список раскрывается ТОЛЬКО по явному действию пользователя — клик, ввод текста или
        // ArrowDown (см. onClick/onChange/onKeyDown). Открытие по onFocus убрано намеренно
        // (issue #5): Radix Dialog при открытии ставит автофокус на первое поле («Подразделение»),
        // и раскрытие по фокусу разворачивало список сразу при открытии диалога, хотя пользователь
        // с полем не взаимодействовал. Клавиатурный доступ сохранён через ArrowDown.
        onClick={() => setOpen(true)}
        onBlur={() => {
          // Фокус ушёл в блок создания (портал) — список остаётся открытым.
          if (creatingRef.current) return
          blurTimer.current = setTimeout(close, 150)
        }}
      />
      {visible && pos
        ? createPortal(
            <ul
              ref={listRef}
              id={listboxId}
              className={cn(
                'ds-surface pointer-events-auto fixed z-popover max-h-56 overflow-auto rounded-8 py-1 shadow-elevated',
                workScale,
              )}
              // pointerEvents inline (не только Tailwind-класс): модальный Radix Dialog ставит
              // pointer-events:none на <body>, портал-потомок его наследует.
              style={{ top: pos.top, left: pos.left, width: pos.width, pointerEvents: 'auto' }}
              role="listbox"
            >
              {hasCreate && !creating ? (
                // Клавиатура combobox'а живёт на инпуте (`aria-activedescendant`) — сама строка
                // по паттерну WAI-ARIA не фокусируется и своих keyboard-обработчиков не имеет.
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  role="option"
                  aria-selected={false}
                  className="cursor-pointer px-3 py-2 text-14 font-medium text-accent hover:bg-hover"
                  // preventDefault на mousedown удерживает фокус в инпуте до onClick.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={openCreate}
                >
                  {DEPARTMENT_SELECT_ADD}
                </li>
              ) : null}
              {onCreate && creating ? (
                <li className="px-3 py-2">
                  <label
                    className="mb-1.5 block text-13 text-txt-secondary"
                    htmlFor={`${reactId}-new`}
                  >
                    {DEPARTMENT_SELECT_NAME_LABEL}
                    <RequiredMark />
                  </label>
                  <Input
                    ref={newNameRef}
                    id={`${reactId}-new`}
                    // Подпись помечена «*», а поле живёт внутри списка мимо `Field` —
                    // обязательность объявляем сами. Без наименования подразделение не создать.
                    aria-required
                    value={newName}
                    placeholder={DEPARTMENT_SELECT_NAME_PLACEHOLDER}
                    invalid={Boolean(createError)}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      // «Enter выполняет действие «Добавить», если введено допустимое наименование».
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        void create()
                      }
                    }}
                  />
                  {createError ? (
                    <p role="alert" className="mt-1.5 text-12 text-red">
                      {createError}
                    </p>
                  ) : null}
                  <div className="mt-2 flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!canCreate}
                      // preventDefault на mousedown удерживает фокус в поле создания.
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => void create()}
                    >
                      {DEPARTMENT_SELECT_CREATE}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={cancelCreateAndFocus}
                    >
                      {DEPARTMENT_SELECT_CANCEL}
                    </Button>
                  </div>
                </li>
              ) : null}
              {/* Разделительная черта — непосредственно после служебной опции создания. */}
              {hasCreate ? (
                <li
                  aria-hidden
                  className="-mx-1 my-1 h-px bg-line"
                  data-testid="select-separator"
                />
              ) : null}
              {options.length === 0 ? (
                <li className="px-3 py-2 text-13 text-txt-tertiary">{DEPARTMENT_SELECT_EMPTY}</li>
              ) : (
                options.map((d, i) => (
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                  <li
                    key={d.id}
                    id={rowId(i)}
                    role="option"
                    aria-selected={d.name === value}
                    className={
                      'cursor-pointer px-3 py-2 text-14 text-txt hover:bg-hover' +
                      (i === active ? ' bg-hover' : '')
                    }
                    onMouseDown={(e) => e.preventDefault()}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(d)}
                  >
                    {d.name}
                  </li>
                ))
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  )
}
